import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import { Close, OpenInFull, Minimize } from "@mui/icons-material";
import { useAssistantStore } from "../../stores/assistant";
import { AssistantChat } from "./AssistantChat";

const getContextLabel = (context, assignmentName, className) => {
  if (!context) return "";

  if (context.assignment_id && context.class_id) {
    if (assignmentName && className) {
      return `${assignmentName} - ${className}`;
    } else if (assignmentName) {
      return assignmentName;
    }
    return "Assignment";
  } else if (context.assignment_id) {
    return assignmentName || "Assignment";
  } else if (context.class_id) {
    return className || "Class";
  }
  return "";
};

export const EmbeddedAssistantChat = () => {
  const {
    isEmbeddedOpen,
    isMinimized,
    currentContext,
    conversationId,
    assignmentName,
    className,
    closeEmbedded,
    minimizeEmbedded,
    restoreEmbedded,
  } = useAssistantStore();
  const navigate = useNavigate();

  if (!isEmbeddedOpen) return null;

  const contextLabel = getContextLabel(
    currentContext,
    assignmentName,
    className,
  );

  const handleExpand = () => {
    if (conversationId) {
      navigate(`/assistant?conversation=${conversationId}`);
    } else {
      useAssistantStore.getState().setFullPageLaunch(currentContext);
      navigate(
        `/assistant?new=true&launch=true${currentContext?.creation?.kind ? `&create=${currentContext.creation.kind}` : ""}`,
      );
    }
    closeEmbedded();
  };

  // Minimized view - small bubble
  if (isMinimized) {
    return (
      <Paper
        elevation={8}
        onClick={restoreEmbedded}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          width: 300,
          zIndex: 1300,
          borderRadius: 2,
          cursor: "pointer",
          "&:hover": {
            elevation: 12,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
            backgroundColor: "primary.light",
            color: "primary.contrastText",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            Bud{contextLabel && ` (${contextLabel})`}
          </Typography>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                closeEmbedded();
              }}
              sx={{ color: "inherit" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    );
  }

  // Full view
  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: { xs: "calc(100vw - 32px)", sm: 400 },
        height: { xs: "calc(100vh - 100px)", sm: 550 },
        maxHeight: "calc(100vh - 100px)",
        zIndex: 1300,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "primary.light",
          color: "primary.contrastText",
        }}
      >
        <Typography variant="subtitle1" fontWeight="medium">
          Bud{contextLabel && ` (${contextLabel})`}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Minimize">
            <IconButton
              size="small"
              onClick={minimizeEmbedded}
              sx={{ color: "inherit" }}
            >
              <Minimize fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Expand to full page">
            <IconButton
              size="small"
              onClick={handleExpand}
              sx={{ color: "inherit" }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={closeEmbedded}
              sx={{ color: "inherit" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <AssistantChat
          initialContext={currentContext}
          conversationId={conversationId}
          mode="embedded"
        />
      </Box>
    </Paper>
  );
};
